import React, { useState, useRef, useMemo } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const VIEW_BASE_WIDTH_PX = 1000; // bề rộng “thiết kế” gốc
const PREVIEW_SCALE = 1.0; // phóng to/thu nhỏ khi xem trong modal
const EXPORT_SCALE = 3; // độ nét khi chụp DOM -> canvas
const PDF_OPTIONS = {
  orientation: "p",
  unit: "mm",
  format: "a4",
  marginMm: 0,
  widthMm: 210,
};

const DEFAULT_TEMPLATE = {
  background: "/template1.png",
  title: {
    style: {
      left: "10.6%",
      top: "43%",
      width: "28%",
      height: "6.5%",
      color: "#e5c57a",
    },
  },
  topic: {
    style: {
      left: "10.6%",
      top: "48%",
      width: "28%",
      height: "6.5%",
      color: "#e5c57a",
    },
  },
  content: {
    style: {
      left: "52.3%",
      top: "13.3%",
      width: "36.5%",
      height: "48%",
      padding: "2%",
      color: "#fff",
    },
  },
  time: {
    style: {
      left: "2%",
      top: "55%",
      width: "45%",
      height: "6%",
      color: "#e5c57a",
    },
  },
  location: {
    style: {
      left: "2%",
      top: "60.5%",
      width: "45%",
      height: "6%",
      color: "#e5c57a",
    },
  },
  note: {
    style: {
      left: "55.8%",
      top: "85.6%",
      width: "27.8%",
      height: "10.2%",
      color: "#e5c57a",
    },
  },
  qrCode: {
    style: { left: "59.3%", top: "63.8%", width: "9.3%", height: "12.3%" },
  },
};

const stripHtml = (html = "") => {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent || div.innerText || "";
};

const safeParseSchema = (raw) => {
  if (!raw) return null;

  let schema = raw;

  for (let i = 0; i < 2; i += 1) {
    if (typeof schema === "string") {
      try {
        schema = JSON.parse(schema);
      } catch {
        return null;
      }
    }
  }

  if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
    return null;
  }

  return schema;
};

const normalizeLayer = (layer = {}) => {
  const type = layer.type === "image" ? "image" : "text";
  const style = layer.style || {};

  return {
    id: layer.id || `${type}_${Math.random().toString(16).slice(2)}`,
    type,
    left: Number(layer.left ?? 0),
    top: layer.bottom != null ? undefined : Number(layer.top ?? 0),
    bottom: layer.bottom != null ? Number(layer.bottom) : undefined,
    width: Number(layer.width ?? 10),
    height: Number(layer.height ?? 6),
    zIndex: Number(layer.zIndex ?? 10),

    text: type === "text" ? String(layer.text ?? "") : undefined,

    src: type === "image" ? String(layer.src ?? "") : undefined,
    sourceKey: type === "image" ? String(layer.sourceKey ?? "") : undefined,
    fit:
      type === "image"
        ? layer.fit === "contain"
          ? "contain"
          : "cover"
        : undefined,
    circle: type === "image" ? Boolean(layer.circle) : undefined,

    style:
      type === "text"
        ? {
            fontFamily: style.fontFamily || "",
            fontSize: Number(style.fontSize ?? 18),
            fontWeight: String(style.fontWeight ?? "400"),
            color: style.color || "#ffffff",
            align: style.align || "left",
            uppercase: Boolean(style.uppercase),
            lineHeight: Number(style.lineHeight ?? 1.25),
          }
        : undefined,
  };
};

const normalizeTemplateSchema = (schema) => {
  if (!schema || typeof schema !== "object") return null;

  const backgroundSrc = schema.backgroundSrc || schema.background || "";

  if (!backgroundSrc) return null;

  return {
    schemaVersion: schema.schemaVersion || 2,
    backgroundSrc,
    layers: Array.isArray(schema.layers)
      ? schema.layers
          .map((layer) => normalizeLayer(layer))
          .sort((a, b) => Number(a.zIndex || 0) - Number(b.zIndex || 0))
      : [],
  };
};

const formatContentRows = (contentRows = []) => {
  if (!Array.isArray(contentRows)) return "";

  return contentRows
    .filter(
      (item) => `${item?.time || ""}`.trim() || `${item?.value || ""}`.trim(),
    )
    .map((item) => {
      const time = `${item?.time || ""}`.trim();
      const value = `${item?.value || ""}`.trim();

      if (time && value) return `${time}  ${value}`;
      return time || value;
    })
    .join("\n");
};

const renderTemplateText = (text = "", data, contentRows) => {
  const values = {
    title: data?.title || "",
    topic: data?.topic || "",
    time: data?.time || "",
    location: stripHtml(data?.location || ""),
    note: stripHtml(data?.note || ""),
    content: formatContentRows(contentRows),
  };

  return String(text || "").replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (m, key) => {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      return values[key];
    }

    return m;
  });
};

const resolveImageSrc = (layer, data) => {
  if (!layer) return "";

  if (layer.sourceKey === "event.qrCode") return data?.qrCode || "";
  if (layer.sourceKey === "qrCode") return data?.qrCode || "";
  if (layer.sourceKey === "{{qrCode}}") return data?.qrCode || "";

  return layer.src || "";
};

const LayerText = ({ layer, data, contentRows }) => {
  const st = layer.style || {};

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        fontFamily: st.fontFamily || undefined,
        fontSize: `${Number(st.fontSize ?? 18)}px`,
        fontWeight: st.fontWeight || "400",
        color: st.color || "#ffffff",
        textAlign: st.align || "left",
        lineHeight: Number(st.lineHeight ?? 1.25),
        textTransform: st.uppercase ? "uppercase" : "none",
        whiteSpace: "pre-wrap",
        overflow: "hidden",
        wordBreak: "break-word",
      }}
    >
      {renderTemplateText(layer.text || "", data, contentRows)}
    </div>
  );
};

const LayerImage = ({ layer, data }) => {
  const src = resolveImageSrc(layer, data);
  const circle = Boolean(layer.circle);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: circle ? "9999px" : "12px",
        overflow: "hidden",
        backgroundColor: src ? "transparent" : "rgba(255,255,255,0.25)",
        border: src ? "none" : "1px dashed rgba(255,255,255,0.6)",
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          crossOrigin="anonymous"
          className="w-full h-full block"
          style={{
            objectFit: layer.fit === "contain" ? "contain" : "cover",
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/70 text-xs">
          Ảnh
        </div>
      )}
    </div>
  );
};

const DatabaseTemplateInvite = ({ schema, data, contentRows, previewWidth }) => {
  return (
    <div className="relative mx-auto" style={{ width: previewWidth }}>
      <img
        src={schema.backgroundSrc}
        alt="Background"
        className="w-full h-auto block"
        crossOrigin="anonymous"
      />

      {(schema.layers || []).map((layer) => {
        const hasBottom = layer.bottom !== undefined && layer.bottom !== null;
        const isCircleImg = layer.type === "image" && layer.circle;

        const wrapperStyle = {
          position: "absolute",
          left: `${Number(layer.left ?? 0)}%`,
          width: `${Number(layer.width ?? 10)}%`,
          zIndex: Number(layer.zIndex ?? 1),
          ...(hasBottom
            ? { bottom: `${Number(layer.bottom ?? 0)}%` }
            : { top: `${Number(layer.top ?? 0)}%` }),
          ...(isCircleImg
            ? { aspectRatio: "1 / 1" }
            : { height: `${Number(layer.height ?? 6)}%` }),
        };

        return (
          <div key={layer.id} style={wrapperStyle}>
            {layer.type === "image" ? (
              <LayerImage layer={layer} data={data} />
            ) : (
              <LayerText layer={layer} data={data} contentRows={contentRows} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const DefaultTemplateInvite = ({ tpl, data, contentRows, previewWidth }) => {
  return (
    <div className="relative mx-auto" style={{ width: previewWidth }}>
      <img
        src={tpl.background}
        alt="Background"
        className="w-full h-auto block"
      />

      {tpl.title && (
        <div
          className="absolute flex items-center justify-center text-white font-bold uppercase text-[10px] md:text-sm lg:text-base text-center px-1"
          style={tpl.title.style}
        >
          {data?.title || ""}
        </div>
      )}

      {tpl.topic && (
        <div
          className="absolute flex items-center justify-center text-white font-bold uppercase text-[10px] md:text-sm lg:text-base text-center px-1"
          style={tpl.topic.style}
        >
          {data?.topic || ""}
        </div>
      )}

      {tpl.content && (
        <div
          className="absolute bg-transparent text-slate-800 overflow-hidden"
          style={tpl.content.style}
        >
          <div className="mt-0 space-y-2 text-[9px] md:text-[11px] lg:text-[13px] leading-snug overflow-y-auto h-[100%] pr-1">
            {contentRows.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <p className="font-semibold min-w-[70px]">{item.time}</p>
                <p className="flex-1">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tpl.time && (
        <div
          className="absolute flex items-center justify-center text-blue-900 font-bold text-[10px] md:text-sm lg:text-base text-center px-2"
          style={tpl.time.style}
        >
          {data?.time || ""}
        </div>
      )}

      {tpl.location && (
        <div
          className="absolute flex items-center justify-center font-bold text-[9px] md:text-sm lg:text-base text-center px-2"
          style={tpl.location.style}
        >
          <span
            className="sun-editor-editable !bg-transparent break-words !text-white"
            dangerouslySetInnerHTML={{ __html: data?.location || "" }}
          />
        </div>
      )}

      {tpl.note && (
        <div
          className="absolute text-[9px] md:text-[11px] lg:text-[13px] leading-snug "
          style={tpl.note.style}
        >
          <span
            className="sun-editor-editable !bg-transparent break-words !text-white"
            dangerouslySetInnerHTML={{ __html: data?.note || "" }}
          />
        </div>
      )}

      {tpl.qrCode && data?.qrCode && (
        <img
          src={data.qrCode}
          alt="QR"
          className="absolute"
          style={tpl.qrCode.style}
          crossOrigin="anonymous"
        />
      )}
    </div>
  );
};

const EventPDFModal = ({ isOpen, onClose, data }) => {
  const [isLoading, setIsLoading] = useState(false);
  const pdfRef = useRef();
  const tpl = DEFAULT_TEMPLATE;

  const content = useMemo(() => {
    try {
      return JSON.parse(data?.content ?? "[]");
    } catch {
      return [];
    }
  }, [data?.content]);

  const hasCustomInvite = !!(
    data?.eventInvationImage && `${data.eventInvationImage}`.trim()
  );

  const templateSchema = useMemo(() => {
    if (hasCustomInvite) return null;

    const rawSchema = data?.eventInvitationCardTemplate?.schema;
    const parsedSchema = safeParseSchema(rawSchema);

    return normalizeTemplateSchema(parsedSchema);
  }, [data?.eventInvitationCardTemplate?.schema, hasCustomInvite]);

  const handleDownloadPDF = async () => {
    if (!pdfRef.current) return;
    setIsLoading(true);

    const elements = pdfRef.current.children;
    const pdf = new jsPDF({
      orientation: PDF_OPTIONS.orientation,
      unit: PDF_OPTIONS.unit,
      format: PDF_OPTIONS.format,
    });

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      const canvas = await html2canvas(element, {
        useCORS: true,
        scale: EXPORT_SCALE,
        backgroundColor: null,
      });

      const dataURL = canvas.toDataURL("image/png");
      const imgWidth = PDF_OPTIONS.widthMm - PDF_OPTIONS.marginMm * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const x = PDF_OPTIONS.marginMm;
      const y = PDF_OPTIONS.marginMm;

      if (i > 0) pdf.addPage();
      pdf.addImage(dataURL, "PNG", x, y, imgWidth, imgHeight);
    }

    pdf.save("THIEP_MOI_CCA.pdf");
    setIsLoading(false);
  };

  if (!isOpen) return null;

  const previewWidth = `${VIEW_BASE_WIDTH_PX * PREVIEW_SCALE}px`;

  return (
    <div className="fixed inset-0 bg-gray-500/50 flex justify-center items-center z-50">
      <div className="bg-base-100 p-0 lg:p-6 rounded-lg w-5/6 lg:w-3/4 max-h-[90vh] overflow-y-auto">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-end mb-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 btn btn-outline rounded btn-sm"
            >
              X
            </button>
          </div>

          <div className="space-y-5" ref={pdfRef}>
            {hasCustomInvite ? (
              <div className="relative mx-auto" style={{ width: previewWidth }}>
                <img
                  src={data.eventInvationImage}
                  alt="Thiệp mời sự kiện"
                  className="w-full h-auto block"
                  crossOrigin="anonymous"
                />
              </div>
            ) : templateSchema ? (
              <DatabaseTemplateInvite
                schema={templateSchema}
                data={data}
                contentRows={content}
                previewWidth={previewWidth}
              />
            ) : (
              <DefaultTemplateInvite
                tpl={tpl}
                data={data}
                contentRows={content}
                previewWidth={previewWidth}
              />
            )}
          </div>

          <div className="flex justify-end mt-8">
            <button
              disabled={isLoading}
              type="button"
              onClick={handleDownloadPDF}
              className="px-4 py-2 btn btn-outline rounded btn-sm btn-success"
            >
              {isLoading ? "Đang xử lý ..." : "Xuất PDF"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPDFModal;